import React from 'react';
import { motion } from 'framer-motion';
import { Newspaper, Image, Video, FileText, Calendar, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const newsArticles = [
  {
    title: 'OPAD Launches New Climate Initiative in East Africa',
    date: 'December 10, 2024',
    category: 'Press Release',
    summary: 'A comprehensive climate action program targeting 10,000 farmers across Kenya and Uganda.',
    image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Annual Report 2024: Record Impact Achieved',
    date: 'December 1, 2024',
    category: 'News',
    summary: 'Our latest annual report shows unprecedented growth in beneficiaries reached and projects completed.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Partnership with UN Expanded',
    date: 'November 15, 2024',
    category: 'Press Release',
    summary: 'OPAD strengthens collaboration with United Nations agencies for humanitarian response.',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'New Education Center Opens in Ghana',
    date: 'November 1, 2024',
    category: 'News',
    summary: 'State-of-the-art learning facility to serve 500+ students in underserved communities.',
    image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?crop=entropy&cs=srgb&fm=jpg&q=85'
  }
];

const publications = [
  { title: 'Annual Report 2024', type: 'PDF', size: '4.2 MB' },
  { title: 'Impact Assessment 2023', type: 'PDF', size: '2.8 MB' },
  { title: 'Climate Action Strategy', type: 'PDF', size: '1.5 MB' },
  { title: 'Gender Equality Policy', type: 'PDF', size: '890 KB' }
];

const galleryImages = [
  { url: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?crop=entropy&cs=srgb&fm=jpg&q=85', caption: 'Community gathering in Kenya' },
  { url: 'https://images.unsplash.com/photo-1574465636377-7781c5117a0c?crop=entropy&cs=srgb&fm=jpg&q=85', caption: 'Education program' },
  { url: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?crop=entropy&cs=srgb&fm=jpg&q=85', caption: 'Agricultural support' },
  { url: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?crop=entropy&cs=srgb&fm=jpg&q=85', caption: 'Humanitarian response' },
  { url: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85', caption: 'Health clinic' },
  { url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85', caption: 'Team collaboration' }
];

const upcomingEvents = [
  { title: 'Global Volunteer Summit 2025', date: 'January 15-17, 2025', location: 'Brussels, Belgium' },
  { title: 'Climate Action Webinar', date: 'January 25, 2025', location: 'Online' },
  { title: 'Annual Donor Appreciation', date: 'February 10, 2025', location: 'New York, USA' }
];

export default function MediaPage() {
  return (
    <div data-testid="media-page">
      {/* Hero Section */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Media & Resources
              </h1>
              <p className="text-lg text-slate-600">
                Stay updated with our latest news, publications, and multimedia content.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <Tabs defaultValue="news" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-8 bg-slate-100 p-1 rounded-full">
              <TabsTrigger 
                value="news" 
                className="rounded-full data-[state=active]:bg-[#7BC4E2] data-[state=active]:text-white"
                data-testid="tab-news"
              >
                <Newspaper className="w-4 h-4 mr-2" />
                News
              </TabsTrigger>
              <TabsTrigger 
                value="publications"
                className="rounded-full data-[state=active]:bg-[#7BC4E2] data-[state=active]:text-white"
                data-testid="tab-publications"
              >
                <FileText className="w-4 h-4 mr-2" />
                Publications
              </TabsTrigger>
              <TabsTrigger 
                value="gallery"
                className="rounded-full data-[state=active]:bg-[#7BC4E2] data-[state=active]:text-white"
                data-testid="tab-gallery"
              >
                <Image className="w-4 h-4 mr-2" />
                Gallery
              </TabsTrigger>
              <TabsTrigger 
                value="events"
                className="rounded-full data-[state=active]:bg-[#7BC4E2] data-[state=active]:text-white"
                data-testid="tab-events"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Events
              </TabsTrigger>
            </TabsList>

            {/* News Tab */}
            <TabsContent value="news" data-testid="news-content">
              <div className="grid md:grid-cols-2 gap-8">
                {newsArticles.map((article, index) => (
                  <motion.div
                    key={index}
                    className="bg-slate-50 rounded-2xl overflow-hidden card-lift"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    data-testid={`news-article-${index}`}
                  >
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-medium text-[#7BC4E2] bg-[#7BC4E2]/10 px-2 py-1 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-sm text-slate-500">{article.date}</span>
                      </div>
                      <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{article.summary}</p>
                      <Button variant="link" className="p-0 text-[#7BC4E2] hover:text-[#6AB3D1]">
                        Read More <ExternalLink className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* Publications Tab */}
            <TabsContent value="publications" data-testid="publications-content">
              <div className="max-w-2xl mx-auto">
                <div className="space-y-4">
                  {publications.map((pub, index) => (
                    <motion.div
                      key={index}
                      className="bg-slate-50 p-6 rounded-2xl flex items-center justify-between card-lift"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`publication-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#E08B7A]/10 rounded-xl flex items-center justify-center">
                          <FileText className="w-6 h-6 text-[#E08B7A]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{pub.title}</h3>
                          <p className="text-sm text-slate-500">{pub.type} • {pub.size}</p>
                        </div>
                      </div>
                      <Button variant="outline" className="rounded-full">
                        Download
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" data-testid="gallery-content">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryImages.map((img, index) => (
                  <motion.div
                    key={index}
                    className="relative group overflow-hidden rounded-2xl"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    data-testid={`gallery-image-${index}`}
                  >
                    <img
                      src={img.url}
                      alt={img.caption}
                      className="w-full h-48 md:h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <p className="text-white text-sm">{img.caption}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events" data-testid="events-content">
              <div className="max-w-2xl mx-auto">
                <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-6">Upcoming Events</h3>
                <div className="space-y-4">
                  {upcomingEvents.map((event, index) => (
                    <motion.div
                      key={index}
                      className="bg-slate-50 p-6 rounded-2xl card-lift"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`event-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-[#7BC4E2] rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0">
                          <Calendar className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-1">{event.title}</h4>
                          <p className="text-sm text-slate-600">{event.date}</p>
                          <p className="text-sm text-slate-500">{event.location}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
